import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useLocale } from 'escrowbuild-ui';

import { useT } from '@/i18n';
import { TITLE_SUFFIX, pageTitle } from './nav';

/**
 * Names the current page in the document title.
 *
 * Every route used to share the title `escrowbuild.io`, so the browser tab was
 * identical on all eighteen screens — no way to tell a dashboard from a report
 * from an investor's portal in a tab strip or a window switcher. This derives
 * the page's name from the same registry that drives the navigation, so a page
 * cannot be added without its title following, and the two can never disagree.
 *
 * Written as a hook rather than a `<title>` element because the title belongs to
 * the document, not to the React tree — rendering it as a component would put a
 * `<title>` inside `<body>`, which is invalid and which browsers ignore.
 *
 * Both shells call it, so the platform and the portal are named by one rule.
 *
 * `locale` is in the dependency list beside `pathname` because the title is a
 * translated string: a language change has to re-run this even though the route
 * has not moved, or every open tab keeps naming the previous language's page
 * until the reader navigates somewhere else.
 */
export function usePageTitle(): void {
  const { pathname } = useLocation();
  const t = useT();
  const { locale } = useLocale();

  useEffect(() => {
    document.title = `${pageTitle(pathname, t)} · ${TITLE_SUFFIX}`;
  }, [pathname, locale, t]);
}
